import React, { useState, useEffect } from "react";
import { Search, Check, RefreshCw, Star, Clock, Filter, Grid, List } from "lucide-react";

const TemplateSelectionStep = ({
  entryType,
  selectedTemplate,
  onSelect,
  templates = [],
  loading = false,
  error = null,
  onRetry,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("alla");
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [viewMode, setViewMode] = useState("compact"); // compact, list, or grid
  const [showFilters, setShowFilters] = useState(false);

  // Reset filters when entry type changes
  useEffect(() => {
    setSearchQuery("");
    setCategoryFilter("alla");
    setShowAllTemplates(false);
  }, [entryType]);

  // Your existing hardcoded templates...
  const hardcodedTemplates = [
    {
      id: "invoice-payment",
      name: "Kundbetalning",
      description: "Registrera betalning från kund för faktura",
      entryType: "income",
      category: "försäljning",
      common: true,
      accounts: ["1510 - Kundfordringar", "3010 - Försäljningsintäkter"],
      popularity: 95
    },
    {
      id: "sales-receipt",
      name: "Kontantförsäljning",
      description: "Registrera direkt kontant- eller kortförsäljning",
      entryType: "income",
      category: "försäljning",
      common: true,
      accounts: ["1910 - Kassa", "3010 - Försäljningsintäkter"],
      popularity: 90
    },
    {
      id: "service-income",
      name: "Tjänsteintäkter",
      description: "Registrera intäkter från tjänster",
      entryType: "income",
      category: "tjänster",
      common: true,
      accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"],
      popularity: 85
    },
    {
      id: "expense-receipt",
      name: "Inköpskvitto",
      description: "Registrera en allmän affärsutgift",
      entryType: "expense",
      category: "inköp",
      common: true,
      accounts: ["1910 - Kassa", "4010 - Driftskostnader"],
      popularity: 92
    },
    {
      id: "supplier-invoice",
      name: "Leverantörsfaktura",
      description: "Registrera betalning av leverantörsfaktura",
      entryType: "expense",
      category: "inköp",
      common: true,
      accounts: ["2410 - Leverantörsskulder", "4010 - Driftskostnader"],
      popularity: 88
    },
    // Add more templates as needed...
  ];

  // Use hardcoded templates if no API templates provided
  const allTemplates = templates.length > 0 ? templates : hardcodedTemplates;

  // Loading and error states remain the same...
  if (loading) {
    return (
      <div className="enhanced-step-content">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ margin: '0 auto 1rem', width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p style={{ color: '#6b7280' }}>Laddar mallar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enhanced-step-content">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  background: 'transparent',
                  color: '#3b82f6',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw size={16} />
                Försök igen
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Filter logic
  const filteredByType = allTemplates.filter(template =>
    entryType ? template.entryType === entryType : true
  );

  const filteredBySearch = searchQuery
    ? filteredByType.filter(template =>
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : filteredByType;

  const filteredTemplates = categoryFilter === "alla"
    ? filteredBySearch
    : filteredBySearch.filter(template => template.category === categoryFilter);

  // Get popular templates
  const popularTemplates = filteredByType
    .filter(t => t.popularity && t.popularity > 80)
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 4); // Show fewer popular templates

  // Per-tenant "Senast använda" (DEFERRED 48). Backend joins usage stats
  // onto each template row; we sort by lastUsedAt desc and show the top 5.
  // Falls back to use count when timestamps tie; templates never used
  // by anyone in the tenant are excluded.
  const recentlyUsedTemplates = filteredByType
    .filter(t => t.lastUsedAt)
    .sort((a, b) => {
      const ta = new Date(a.lastUsedAt).getTime();
      const tb = new Date(b.lastUsedAt).getTime();
      if (tb !== ta) return tb - ta;
      return (b.useCount || 0) - (a.useCount || 0);
    })
    .slice(0, 5);

  // Get available categories
  const availableCategories = [...new Set(filteredByType.map(template => template.category))];

  // Group templates by category
  const templatesByCategory = {};
  filteredTemplates.forEach(template => {
    if (!templatesByCategory[template.category]) {
      templatesByCategory[template.category] = [];
    }
    templatesByCategory[template.category].push(template);
  });

  const formatCategory = (category) => {
    const categoryTranslations = {
      // Backend taxonomy (DEFERRED 59) — keep in sync with
      // accountingtemplates/services/BookkeepingTemplateService.js
      // TEMPLATE_CATEGORIES.
      'forsaljning': 'Försäljning',
      'inkop_varor': 'Inköp av varor',
      'inkop_tjanster': 'Inköp av tjänster',
      'inventarier': 'Inventarier & avskrivningar',
      'personal_lon': 'Personal & lön',
      'bil_transport': 'Bil & transport',
      'representation': 'Möten & representation',
      'bank_avgifter': 'Bank, ränta & avgifter',
      'skatt_moms': 'Skatt & moms',
      'eget_kapital': 'Eget kapital & utdelning',
      'utlagg': 'Utlägg & privatkonton',
      'periodiseringar': 'Periodiseringar & bokslut',
      'ovrigt': 'Övrigt',
      // Legacy hardcoded-template / derived-category keys, kept so the
      // fallback bucket from mapApiTypeToCategory + the in-file
      // hardcodedTemplates still render with Swedish labels.
      'försäljning': 'Försäljning',
      'tjänster': 'Tjänster',
      'fastighet': 'Fastighet',
      'finansiellt': 'Finansiellt',
      'inköp': 'Inköp',
      'personal': 'Personal',
      'lokaler': 'Lokaler',
      'resor': 'Resor',
      'skatt': 'Skatt',
      'försäkring': 'Försäkring',
      'bank': 'Bank',
      'eget-kapital': 'Eget Kapital',
      'redovisning': 'Redovisning',
      'sales': 'Försäljning',
      'purchases': 'Inköp',
      'banking': 'Bank',
      'taxes': 'Skatt',
      'salary': 'Lön',
      'accounting': 'Bokslut',
      'other': 'Övrigt',
    };
    if (!category) return 'Övrigt';
    return categoryTranslations[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getBadgeColorClass = (category) => {
    const colors = {
      // Backend taxonomy (DEFERRED 59).
      'forsaljning':     'var(--bs-success)',
      'inkop_varor':     'var(--bs-primary)',
      'inkop_tjanster':  'var(--bs-primary)',
      'inventarier':     'var(--bs-info)',
      'personal_lon':    'var(--bs-cyan)',
      'bil_transport':   'var(--bs-secondary)',
      'representation':  'var(--bs-warning)',
      'bank_avgifter':   'var(--bs-cyan)',
      'skatt_moms':      'var(--bs-warning)',
      'eget_kapital':    'var(--bs-primary)',
      'utlagg':          'var(--bs-secondary)',
      'periodiseringar': 'var(--bs-dark)',
      'ovrigt':          'var(--bs-secondary)',
      // Legacy keys (hardcoded + derived categories).
      'försäljning':     'var(--bs-success)',
      'inköp':           'var(--bs-primary)',
      'skatt':           'var(--bs-warning)',
      'personal':        'var(--bs-cyan)',
      'resor':           'var(--bs-secondary)',
      'lokaler':         'var(--bs-dark)',
      'redovisning':     'var(--bs-secondary)',
      'bank':            'var(--bs-cyan)',
      'eget-kapital':    'var(--bs-primary)',
      'försäkring':      'var(--bs-dark)',
      'finansiellt':     'var(--bs-success)',
      'fastighet':       'var(--bs-success)',
      'tjänster':        'var(--bs-success)',
      'sales':           'var(--bs-success)',
      'purchases':       'var(--bs-primary)',
      'banking':         'var(--bs-cyan)',
      'taxes':           'var(--bs-warning)',
      'salary':          'var(--bs-cyan)',
      'accounting':      'var(--bs-dark)',
      'other':           'var(--bs-secondary)',
    };
    return colors[category] || 'var(--bs-secondary)';
  };

  const getAccountNumber = (account) => {
    if (account === "Olika") return account;
    return account.split(" - ")[0];
  };

  if (allTemplates.length === 0) {
    return (
      <div className="enhanced-step-content">
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Inga mallar tillgängliga</p>
          <button
            onClick={() => onSelect('custom')}
            style={{
              padding: '0.75rem 1.5rem',
              border: '1px solid var(--bs-primary)',
              borderRadius: '8px',
              background: 'transparent',
              color: 'var(--bs-primary)',
              cursor: 'pointer'
            }}
          >
            Skapa anpassad verifikation
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .compact-template-row {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          transition: all 0.2s ease;
          cursor: pointer;
          padding: 0.75rem;
          margin-bottom: 0.5rem;
        }
        
        .compact-template-row:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
          background: #f8faff;
        }
        
        .compact-template-row.selected {
          border-color: var(--bs-primary);
          background: var(--rgba-primary-1);
          box-shadow: 0 2px 8px var(--rgba-primary-2);
        }
        
        .template-grid-item {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          transition: all 0.2s ease;
          cursor: pointer;
          padding: 1rem;
          min-height: 120px;
        }
        
        .template-grid-item:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 4px 12px var(--rgba-primary-1);
          transform: translateY(-1px);
        }
        
        .template-grid-item.selected {
          border-color: var(--bs-primary);
          background: var(--rgba-primary-1);
          box-shadow: 0 4px 12px var(--rgba-primary-2);
        }
        
        .template-list-row {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          transition: all 0.2s ease;
          cursor: pointer;
          padding: 1rem;
          margin-bottom: 0.5rem;
        }
        
        .template-list-row:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }
        
        .template-list-row.selected {
          border-color: var(--bs-primary);
          background: var(--rgba-primary-1);
        }
        
        .view-toggle {
          display: flex;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }
        
        .view-toggle button {
          padding: 0.5rem 0.75rem;
          border: none;
          background: #f9fafb;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }
        
        .view-toggle button.active {
          background: var(--bs-primary);
          color: white;
        }
        
        .search-container {
          position: relative;
        }
        
        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s ease;
        }
        
        .search-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
        }
        
        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }
        
        .popular-section {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
        }
        
        .category-section {
          margin-bottom: 1.5rem;
        }
        
        .category-header {
          display: flex;
          align-items: center;
          padding: 0.5rem 0;
          margin-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
      `}</style>

      <div className="enhanced-step-content">
        {/* Search and Controls */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.75rem', marginBottom: '1rem' }}>
          <div className="search-container">
            <Search className="search-icon" size={16} />
            <input
              type="text"
              className="search-input"
              placeholder="Sök mallar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '0.75rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontSize: '0.875rem',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="alla">Alla kategorier</option>
            {availableCategories.map((category) => {
              const count = filteredByType.filter(t => t.category === category).length;
              return (
                <option key={category} value={category}>
                  {formatCategory(category)} ({count})
                </option>
              );
            })}
          </select>

          <div className="view-toggle">
            <button
              className={viewMode === 'compact' ? 'active' : ''}
              onClick={() => setViewMode('compact')}
              title="Kompakt vy"
            >
              Kompakt
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="Lista"
            >
              <List size={14} />
            </button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Rutnät"
            >
              <Grid size={14} />
            </button>
          </div>
        </div>

        {/* Recently used templates (DEFERRED 48). Only when no search/filter
            is active — once the user filters, they're looking for something
            specific and recent items would just create visual noise. */}
        {!searchQuery && categoryFilter === 'alla' && recentlyUsedTemplates.length > 0 && (
          <div className="popular-section" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Clock size={16} color="var(--bs-info)" />
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
                Senast använda
              </h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {recentlyUsedTemplates.map((template) => (
                <CompactTemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate === template.id}
                  onClick={() => onSelect(template.id)}
                  formatCategory={formatCategory}
                  getBadgeColorClass={getBadgeColorClass}
                  getAccountNumber={getAccountNumber}
                  showPopularity={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Popular Templates */}
        {!searchQuery && categoryFilter === 'alla' && popularTemplates.length > 0 && (
          <div className="popular-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Star size={16} color="var(--bs-warning)" />
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
                Populära mallar
              </h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
              {popularTemplates.map((template) => (
                <CompactTemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate === template.id}
                  onClick={() => onSelect(template.id)}
                  formatCategory={formatCategory}
                  getBadgeColorClass={getBadgeColorClass}
                  getAccountNumber={getAccountNumber}
                  showPopularity={true}
                />
              ))}
            </div>
          </div>
        )}

        {/* Main Templates */}
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {filteredTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Search size={32} style={{ color: '#9ca3af', marginBottom: '0.5rem' }} />
              <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>Inga mallar matchar dina sökkriterier</p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setCategoryFilter("alla");
                }}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #6b7280',
                  borderRadius: '6px',
                  background: 'transparent',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Återställ filter
              </button>
            </div>
          ) : viewMode === 'compact' ? (
            // Compact view - most space efficient
            <div>
              {Object.keys(templatesByCategory).map((category) => (
                <div key={category} className="category-section">
                  <div className="category-header">
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                      {formatCategory(category)}
                    </h3>
                    <div style={{ flex: 1, marginLeft: '1rem' }}></div>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {templatesByCategory[category].length} mallar
                    </span>
                  </div>

                  {templatesByCategory[category].map((template) => (
                    <CompactTemplateRow
                      key={template.id}
                      template={template}
                      isSelected={selectedTemplate === template.id}
                      onClick={() => onSelect(template.id)}
                          formatCategory={formatCategory}
                      getBadgeColorClass={getBadgeColorClass}
                      getAccountNumber={getAccountNumber}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : viewMode === 'list' ? (
            // List view - detailed
            <div>
              {Object.keys(templatesByCategory).map((category) => (
                <div key={category} className="category-section">
                  <div className="category-header">
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                      {formatCategory(category)}
                    </h3>
                  </div>

                  {templatesByCategory[category].map((template) => (
                    <DetailedTemplateRow
                      key={template.id}
                      template={template}
                      isSelected={selectedTemplate === template.id}
                      onClick={() => onSelect(template.id)}
                          formatCategory={formatCategory}
                      getBadgeColorClass={getBadgeColorClass}
                      getAccountNumber={getAccountNumber}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            // Grid view - balanced
            <div>
              {Object.keys(templatesByCategory).map((category) => (
                <div key={category} className="category-section">
                  <div className="category-header">
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                      {formatCategory(category)}
                    </h3>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '0.75rem'
                  }}>
                    {templatesByCategory[category].map((template) => (
                      <GridTemplateCard
                        key={template.id}
                        template={template}
                        isSelected={selectedTemplate === template.id}
                        onClick={() => onSelect(template.id)}
                        formatCategory={formatCategory}
                        getBadgeColorClass={getBadgeColorClass}
                        getAccountNumber={getAccountNumber}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </>
  );
};

// Compact Template Row - Very space efficient
const CompactTemplateRow = ({ template, isSelected, onClick, formatCategory, getBadgeColorClass, getAccountNumber }) => {
  return (
    <div
      className={`compact-template-row ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
    >
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
              {template.name}
            </span>
          </div>
        </div>

        {/* Account numbers */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {template.accounts && template.accounts.slice(0, 2).map((account, index) => (
            <span
              key={index}
              style={{
                background: '#f3f4f6',
                color: '#374151',
                padding: '0.125rem 0.375rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {getAccountNumber(account)}
            </span>
          ))}
          {template.accounts && template.accounts.length > 2 && (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
              +{template.accounts.length - 2}
            </span>
          )}
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <Check size={16} color="var(--bs-primary)" strokeWidth={2} />
        )}
      </div>
    </div>
  );
};

// Compact Template Card for popular section
const CompactTemplateCard = ({ template, isSelected, onClick, formatCategory, getBadgeColorClass, getAccountNumber, showPopularity }) => {
  return (
    <div
      className={`compact-template-row ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
          {template.name}
        </span>
        {isSelected && <Check size={12} color="var(--bs-primary)" />}
        {showPopularity && <Star size={10} color="var(--bs-warning)" />}
      </div>

      <div style={{ display: 'flex', gap: '0.25rem' }}>
        {template.accounts && template.accounts.slice(0, 2).map((account, index) => (
          <span
            key={index}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              padding: '0.125rem 0.25rem',
              borderRadius: '3px',
              fontSize: '0.625rem'
            }}
          >
            {getAccountNumber(account)}
          </span>
        ))}
      </div>
    </div>
  );
};

// Detailed Template Row for list view
const DetailedTemplateRow = ({ template, isSelected, onClick, formatCategory, getBadgeColorClass, getAccountNumber }) => {
  return (
    <div
      className={`template-list-row ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
              {template.name}
            </h4>
            {isSelected && <Check size={16} color="var(--bs-primary)" />}
          </div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>
            {template.description}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                background: getBadgeColorClass(template.category),
                color: 'white',
                padding: '0.125rem 0.375rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {formatCategory(template.category)}
            </span>
            {template.accounts && template.accounts.slice(0, 3).map((account, index) => (
              <span
                key={index}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem'
                }}
              >
                {getAccountNumber(account)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Grid Template Card for grid view
const GridTemplateCard = ({ template, isSelected, onClick, formatCategory, getBadgeColorClass, getAccountNumber }) => {
  return (
    <div
      className={`template-grid-item ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      {isSelected && (
        <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', color: '#10b981' }}>
          <Check size={16} strokeWidth={2} />
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b', margin: 0, flex: 1 }}>
            {template.name}
          </h4>
        </div>

        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            {template.accounts && template.accounts.slice(0, 3).map((account, index) => (
              <span
                key={index}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  padding: '0.125rem 0.375rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              >
                {getAccountNumber(account)}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            style={{
              background: getBadgeColorClass(template.category),
              color: 'white',
              padding: '0.125rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            {formatCategory(template.category)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionStep;


// import React, { useState, useEffect } from "react";
// import { Search, Check, RefreshCw } from "lucide-react";

// const TemplateSelectionStep = ({
//   entryType,
//   selectedTemplate,
//   onSelect,
//   templates = [],
//   loading = false,
//   error = null,
//   onRetry
// }) => {
//   const [searchQuery, setSearchQuery] = useState("");
//   const [categoryFilter, setCategoryFilter] = useState("alla");
//   const [showAllTemplates, setShowAllTemplates] = useState(false);

//   // Reset filters when entry type changes
//   useEffect(() => {
//     setSearchQuery("");
//     setCategoryFilter("alla");
//     setShowAllTemplates(false);
//   }, [entryType]);

//   // Hardcoded templates for testing sticky headers - extensive list
//   const hardcodedTemplates = [
//     // Income templates - Sales category
//     {
//       id: "invoice-payment",
//       name: "Kundbetalning",
//       description: "Registrera betalning från kund för faktura",
//       entryType: "income",
//       category: "försäljning",
//       common: true,
//       accounts: ["1510 - Kundfordringar", "3010 - Försäljningsintäkter"]
//     },
//     {
//       id: "sales-receipt",
//       name: "Kontantförsäljning",
//       description: "Registrera direkt kontant- eller kortförsäljning",
//       entryType: "income",
//       category: "försäljning",
//       common: true,
//       accounts: ["1910 - Kassa", "3010 - Försäljningsintäkter"]
//     },
//     {
//       id: "product-sales",
//       name: "Produktförsäljning",
//       description: "Registrera försäljning av fysiska produkter",
//       entryType: "income",
//       category: "försäljning",
//       common: true,
//       accounts: ["1910 - Kassa", "3010 - Försäljningsintäkter"]
//     },
//     {
//       id: "online-sales",
//       name: "Onlineförsäljning",
//       description: "Registrera e-handel och onlineförsäljning",
//       entryType: "income",
//       category: "försäljning",
//       common: false,
//       accounts: ["1910 - Kassa", "3010 - Försäljningsintäkter"]
//     },
//     {
//       id: "wholesale-sales",
//       name: "Partiförsäljning",
//       description: "Registrera bulkförsäljning till andra företag",
//       entryType: "income",
//       category: "försäljning",
//       common: false,
//       accounts: ["1510 - Kundfordringar", "3010 - Försäljningsintäkter"]
//     },

//     // Income templates - Services category
//     {
//       id: "service-income",
//       name: "Tjänsteintäkter",
//       description: "Registrera intäkter från tjänster",
//       entryType: "income",
//       category: "tjänster",
//       common: true,
//       accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"]
//     },
//     {
//       id: "consulting-income",
//       name: "Konsultintäkter",
//       description: "Registrera intäkter från konsulttjänster",
//       entryType: "income",
//       category: "tjänster",
//       common: false,
//       accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"]
//     },
//     {
//       id: "maintenance-income",
//       name: "Underhållstjänster",
//       description: "Registrera intäkter från underhåll och reparationer",
//       entryType: "income",
//       category: "tjänster",
//       common: false,
//       accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"]
//     },
//     {
//       id: "design-income",
//       name: "Designtjänster",
//       description: "Registrera intäkter från design och kreativa tjänster",
//       entryType: "income",
//       category: "tjänster",
//       common: false,
//       accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"]
//     },
//     {
//       id: "training-income",
//       name: "Utbildningstjänster",
//       description: "Registrera intäkter från utbildning och kurser",
//       entryType: "income",
//       category: "tjänster",
//       common: false,
//       accounts: ["1910 - Kassa", "3310 - Tjänsteintäkter"]
//     },

//     // Income templates - Property category
//     {
//       id: "rental-income",
//       name: "Hyresintäkter",
//       description: "Registrera intäkter från uthyrning",
//       entryType: "income",
//       category: "fastighet",
//       common: false,
//       accounts: ["1910 - Kassa", "3610 - Hyresintäkter"]
//     },
//     {
//       id: "lease-income",
//       name: "Leasingintäkter",
//       description: "Registrera intäkter från utrustningsleasing",
//       entryType: "income",
//       category: "fastighet",
//       common: false,
//       accounts: ["1910 - Kassa", "3610 - Hyresintäkter"]
//     },
//     {
//       id: "subletting-income",
//       name: "Andrahandsuthyrning",
//       description: "Registrera intäkter från andrahandsuthyrning",
//       entryType: "income",
//       category: "fastighet",
//       common: false,
//       accounts: ["1910 - Kassa", "3610 - Hyresintäkter"]
//     },

//     // Income templates - Financial category
//     {
//       id: "interest-income",
//       name: "Ränteintäkter",
//       description: "Registrera ränteintäkter från investeringar eller konton",
//       entryType: "income",
//       category: "finansiellt",
//       common: false,
//       accounts: ["1910 - Kassa", "3910 - Ränteintäkter"]
//     },
//     {
//       id: "dividend-income",
//       name: "Utdelningsintäkter",
//       description: "Registrera intäkter från utdelningar",
//       entryType: "income",
//       category: "finansiellt",
//       common: false,
//       accounts: ["1910 - Kassa", "3920 - Utdelningsintäkter"]
//     },
//     {
//       id: "investment-gain",
//       name: "Investeringsvinster",
//       description: "Registrera vinster från investeringsförsäljningar",
//       entryType: "income",
//       category: "finansiellt",
//       common: false,
//       accounts: ["1910 - Kassa", "3930 - Investeringsvinster"]
//     },

//     // Expense templates - Purchases category
//     {
//       id: "expense-receipt",
//       name: "Inköpskvitto",
//       description: "Registrera en allmän affärsutgift",
//       entryType: "expense",
//       category: "inköp",
//       common: true,
//       accounts: ["1910 - Kassa", "4010 - Driftskostnader"]
//     },
//     {
//       id: "supplier-invoice",
//       name: "Leverantörsfaktura",
//       description: "Registrera betalning av leverantörsfaktura",
//       entryType: "expense",
//       category: "inköp",
//       common: true,
//       accounts: ["2410 - Leverantörsskulder", "4010 - Driftskostnader"]
//     },
//     {
//       id: "inventory-purchase",
//       name: "Lagerinköp",
//       description: "Registrera inköp av lager eller varor",
//       entryType: "expense",
//       category: "inköp",
//       common: true,
//       accounts: ["1910 - Kassa", "1210 - Lager"]
//     },
//     {
//       id: "raw-materials",
//       name: "Råmaterialinköp",
//       description: "Registrera inköp av råmaterial för produktion",
//       entryType: "expense",
//       category: "inköp",
//       common: false,
//       accounts: ["1910 - Kassa", "1220 - Råmaterial"]
//     },
//     {
//       id: "office-supplies",
//       name: "Kontorsmaterial",
//       description: "Registrera inköp av kontorsmaterial och pappersvaror",
//       entryType: "expense",
//       category: "inköp",
//       common: false,
//       accounts: ["1910 - Kassa", "4020 - Kontorsmaterial"]
//     },

//     // Expense templates - Personnel category
//     {
//       id: "salary-payment",
//       name: "Lönutbetalning",
//       description: "Registrera löne- eller löneutbetalningar till anställda",
//       entryType: "expense",
//       category: "personal",
//       common: true,
//       accounts: ["1910 - Kassa", "5010 - Lönekostnader"]
//     },
//     {
//       id: "contractor-payment",
//       name: "Konsultarvode",
//       description: "Registrera betalningar till frilansare och konsulter",
//       entryType: "expense",
//       category: "personal",
//       common: false,
//       accounts: ["1910 - Kassa", "5020 - Konsultkostnader"]
//     },
//     {
//       id: "bonus-payment",
//       name: "Bonusutbetalning",
//       description: "Registrera bonusutbetalningar till anställda",
//       entryType: "expense",
//       category: "personal",
//       common: false,
//       accounts: ["1910 - Kassa", "5030 - Bonuskostnader"]
//     },
//     {
//       id: "benefits-payment",
//       name: "Personalförmåner",
//       description: "Registrera utbetalningar för personalförmåner",
//       entryType: "expense",
//       category: "personal",
//       common: false,
//       accounts: ["1910 - Kassa", "5040 - Förmånskostnader"]
//     },
//     {
//       id: "training-expense",
//       name: "Personalutbildning",
//       description: "Registrera kostnader för personalutbildning och utveckling",
//       entryType: "expense",
//       category: "personal",
//       common: false,
//       accounts: ["1910 - Kassa", "5050 - Utbildningskostnader"]
//     },

//     // Expense templates - Premises category
//     {
//       id: "rent-expense",
//       name: "Hyreskostnad",
//       description: "Registrera betalning för kontor, butik eller utrustningshyra",
//       entryType: "expense",
//       category: "lokaler",
//       common: true,
//       accounts: ["1910 - Kassa", "4110 - Hyreskostnader"]
//     },
//     {
//       id: "utility-payment",
//       name: "Förbrukningskostnader",
//       description: "Registrera betalning för el, vatten, internet, etc.",
//       entryType: "expense",
//       category: "lokaler",
//       common: false,
//       accounts: ["1910 - Kassa", "4120 - Förbrukningskostnader"]
//     },
//     {
//       id: "maintenance-expense",
//       name: "Fastighetsunderhåll",
//       description: "Registrera kostnader för byggnads- och anläggningsunderhåll",
//       entryType: "expense",
//       category: "lokaler",
//       common: false,
//       accounts: ["1910 - Kassa", "4130 - Underhållskostnader"]
//     },
//     {
//       id: "security-expense",
//       name: "Säkerhetstjänster",
//       description: "Registrera kostnader för säkerhetstjänster och utrustning",
//       entryType: "expense",
//       category: "lokaler",
//       common: false,
//       accounts: ["1910 - Kassa", "4140 - Säkerhetskostnader"]
//     },
//     {
//       id: "cleaning-expense",
//       name: "Städtjänster",
//       description: "Registrera kostnader för städning och vaktmästartjänster",
//       entryType: "expense",
//       category: "lokaler",
//       common: false,
//       accounts: ["1910 - Kassa", "4150 - Städkostnader"]
//     },

//     // Expense templates - Travel category
//     {
//       id: "travel-expense",
//       name: "Resekostnader",
//       description: "Registrera kostnader för tjänsteresor",
//       entryType: "expense",
//       category: "resor",
//       common: false,
//       accounts: ["1910 - Kassa", "4210 - Resekostnader"]
//     },
//     {
//       id: "accommodation-expense",
//       name: "Logi",
//       description: "Registrera hotell- och logikostnader",
//       entryType: "expense",
//       category: "resor",
//       common: false,
//       accounts: ["1910 - Kassa", "4220 - Logikostnader"]
//     },
//     {
//       id: "meal-expense",
//       name: "Måltider och representation",
//       description: "Registrera kostnader för måltider och representation",
//       entryType: "expense",
//       category: "resor",
//       common: false,
//       accounts: ["1910 - Kassa", "4230 - Måltidskostnader"]
//     },
//     {
//       id: "transport-expense",
//       name: "Transportkostnader",
//       description: "Registrera taxi-, tåg- och andra transportkostnader",
//       entryType: "expense",
//       category: "resor",
//       common: false,
//       accounts: ["1910 - Kassa", "4240 - Transportkostnader"]
//     },

//     // Expense templates - Tax category
//     {
//       id: "tax-payment",
//       name: "Skattbetalning",
//       description: "Registrera moms-, inkomstskatt eller andra skattbetalningar",
//       entryType: "expense",
//       category: "skatt",
//       common: true,
//       accounts: ["1910 - Kassa", "4710 - Skattkostnader"]
//     },
//     {
//       id: "vat-payment",
//       name: "Momsbetalning",
//       description: "Registrera momsbetalningar till skattemyndigheten",
//       entryType: "expense",
//       category: "skatt",
//       common: false,
//       accounts: ["1910 - Kassa", "4720 - Momskostnader"]
//     },
//     {
//       id: "income-tax",
//       name: "Inkomstskatt",
//       description: "Registrera bolagsskattbetalningar",
//       entryType: "expense",
//       category: "skatt",
//       common: false,
//       accounts: ["1910 - Kassa", "4730 - Inkomstskatt"]
//     },
//     {
//       id: "property-tax",
//       name: "Fastighetsskatt",
//       description: "Registrera fastighetsskattbetalningar",
//       entryType: "expense",
//       category: "skatt",
//       common: false,
//       accounts: ["1910 - Kassa", "4740 - Fastighetsskatt"]
//     },

//     // Expense templates - Insurance category
//     {
//       id: "insurance-payment",
//       name: "Försäkringsbetalning",
//       description: "Registrera företagsförsäkringsbetalningar",
//       entryType: "expense",
//       category: "försäkring",
//       common: false,
//       accounts: ["1910 - Kassa", "4310 - Försäkringskostnader"]
//     },
//     {
//       id: "liability-insurance",
//       name: "Ansvarsförsäkring",
//       description: "Registrera ansvarsförsäkringsbetalningar",
//       entryType: "expense",
//       category: "försäkring",
//       common: false,
//       accounts: ["1910 - Kassa", "4320 - Ansvarsförsäkring"]
//     },
//     {
//       id: "health-insurance",
//       name: "Sjukförsäkring",
//       description: "Registrera sjukförsäkringsbetalningar för anställda",
//       entryType: "expense",
//       category: "försäkring",
//       common: false,
//       accounts: ["1910 - Kassa", "4330 - Sjukförsäkring"]
//     },

//     // Transfer templates - Banking category
//     {
//       id: "transfer",
//       name: "Banköverföring",
//       description: "Flytta pengar mellan bankkonton",
//       entryType: "transfer",
//       category: "bank",
//       common: true,
//       accounts: ["1920 - Bankkonto", "1930 - Sparkonto"]
//     },
//     {
//       id: "deposit",
//       name: "Kontantinsättning",
//       description: "Sätta in kontanter på bankkonto",
//       entryType: "transfer",
//       category: "bank",
//       common: false,
//       accounts: ["1910 - Kassa", "1920 - Bankkonto"]
//     },
//     {
//       id: "withdrawal",
//       name: "Kontantuttag",
//       description: "Ta ut kontanter från bankkonto",
//       entryType: "transfer",
//       category: "bank",
//       common: false,
//       accounts: ["1920 - Bankkonto", "1910 - Kassa"]
//     },
//     {
//       id: "loan-transfer",
//       name: "Lånöverföring",
//       description: "Överför lånemedel till företagskonto",
//       entryType: "transfer",
//       category: "bank",
//       common: false,
//       accounts: ["2110 - Företagslån", "1920 - Bankkonto"]
//     },

//     // Transfer templates - Equity category
//     {
//       id: "owner-deposit",
//       name: "Ägarinsättning",
//       description: "Registrera medel som ägarna tillför företaget",
//       entryType: "transfer",
//       category: "eget-kapital",
//       common: false,
//       accounts: ["1910 - Kassa", "3110 - Eget kapital"]
//     },
//     {
//       id: "owner-withdrawal",
//       name: "Ägaruttag",
//       description: "Registrera medel som ägarna tar ut från företaget",
//       entryType: "transfer",
//       category: "eget-kapital",
//       common: false,
//       accounts: ["3110 - Eget kapital", "1910 - Kassa"]
//     },
//     {
//       id: "capital-injection",
//       name: "Kapitaltillskott",
//       description: "Registrera ytterligare kapitalinvestering",
//       entryType: "transfer",
//       category: "eget-kapital",
//       common: false,
//       accounts: ["1910 - Kassa", "3120 - Aktiekapital"]
//     },

//     // Other templates - Accounting category
//     {
//       id: "opening-balance",
//       name: "Ingående balans",
//       description: "Ställ in initiala kontosaldon",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["Olika"]
//     },
//     {
//       id: "journal-entry",
//       name: "Manuell verifikation",
//       description: "Skapa en anpassad verifikation",
//       entryType: "other",
//       category: "redovisning",
//       common: true,
//       accounts: ["Olika"]
//     },
//     {
//       id: "depreciation",
//       name: "Avskrivning",
//       description: "Registrera tillgångsavskrivning",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["1610 - Ackumulerad avskrivning", "7910 - Avskrivningskostnad"]
//     },
//     {
//       id: "accrual",
//       name: "Periodisering",
//       description: "Registrera intäkter eller kostnader som ännu inte betalats/erhållits",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["Olika"]
//     },
//     {
//       id: "adjustment",
//       name: "Justering",
//       description: "Korrigera eller justera befintliga verifikationer",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["Olika"]
//     },
//     {
//       id: "closing-entry",
//       name: "Avslutande verifikation",
//       description: "Stänga konton vid periodens slut",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["Olika"]
//     },
//     {
//       id: "reclassification",
//       name: "Omklassificering",
//       description: "Omklassificera verifikationer mellan konton",
//       entryType: "other",
//       category: "redovisning",
//       common: false,
//       accounts: ["Olika"]
//     },
//   ];

//   // Use hardcoded templates if no API templates provided (for testing)
//   const allTemplates = templates.length > 0 ? templates : hardcodedTemplates;

//   // If we're loading templates, show loading state
//   if (loading) {
//     return (
//       <div>
//         <h2 className="fs-4 fw-medium mb-3">Välj Mall</h2>
//         <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
//           <div className="text-center">
//             <div className="spinner-border mb-3" role="status">
//               <span className="visually-hidden">Laddar mallar...</span>
//             </div>
//             <p className="text-muted">Laddar mallar...</p>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // If there's an error loading templates
//   if (error) {
//     return (
//       <div>
//         <h2 className="fs-4 fw-medium mb-3">Välj Mall</h2>
//         <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
//           <div className="text-center">
//             <p className="text-danger mb-3">{error}</p>
//             {onRetry && (
//               <button
//                 className="btn btn-outline-primary d-flex align-items-center gap-2 mx-auto"
//                 onClick={onRetry}
//               >
//                 <RefreshCw size={16} />
//                 Försök igen
//               </button>
//             )}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Filter templates by entry type
//   const filteredByType = allTemplates.filter(template =>
//     entryType ? template.entryType === entryType : true
//   );

//   // Apply search filter if present
//   const filteredBySearch = searchQuery
//     ? filteredByType.filter(template =>
//       template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//       template.description.toLowerCase().includes(searchQuery.toLowerCase())
//     )
//     : filteredByType;

//   // Apply category filter if selected
//   const filteredTemplates = categoryFilter === "alla"
//     ? filteredBySearch
//     : filteredBySearch.filter(template => template.category === categoryFilter);

//   // Get available categories for the current entry type
//   const availableCategories = [...new Set(filteredByType.map(template => template.category))];

//   // Group templates by category for sticky headers
//   const templatesByCategory = {};
//   filteredTemplates.forEach(template => {
//     if (!templatesByCategory[template.category]) {
//       templatesByCategory[template.category] = [];
//     }
//     templatesByCategory[template.category].push(template);
//   });

//   // Format category for display
//   const formatCategory = (category) => {
//     const categoryTranslations = {
//       'försäljning': 'Försäljning',
//       'tjänster': 'Tjänster',
//       'fastighet': 'Fastighet',
//       'finansiellt': 'Finansiellt',
//       'inköp': 'Inköp',
//       'personal': 'Personal',
//       'lokaler': 'Lokaler',
//       'resor': 'Resor',
//       'skatt': 'Skatt',
//       'försäkring': 'Försäkring',
//       'bank': 'Bank',
//       'eget-kapital': 'Eget Kapital',
//       'redovisning': 'Redovisning'
//     };
//     return categoryTranslations[category] || category.charAt(0).toUpperCase() + category.slice(1);
//   };

//   // Handle When no templates match the filters
//   const noTemplatesMatch = filteredTemplates.length === 0;

//   // Function to get badge color based on category
//   const getBadgeColorClass = (category) => {
//     switch (category) {
//       case 'försäljning': return 'bg-success';
//       case 'inköp': return 'bg-primary';
//       case 'skatt': return 'bg-warning';
//       case 'personal': return 'bg-info';
//       case 'resor': return 'bg-secondary';
//       case 'lokaler': return 'bg-dark';
//       case 'redovisning': return 'bg-secondary';
//       case 'bank': return 'bg-info';
//       case 'eget-kapital': return 'bg-primary';
//       case 'försäkring': return 'bg-dark';
//       case 'finansiellt': return 'bg-success';
//       case 'fastighet': return 'bg-success';
//       case 'tjänster': return 'bg-success';
//       default: return 'bg-light text-dark';
//     }
//   };

//   // Extract account numbers from full account string (e.g. "1510 - Kundfordringar" -> "1510")
//   const getAccountNumber = (account) => {
//     if (account === "Olika") return account;
//     return account.split(" - ")[0];
//   };

//   // Show message if no templates available
//   if (allTemplates.length === 0) {
//     return (
//       <div>
//         <h2 className="fs-4 fw-medium mb-3">Välj Mall</h2>
//         <div className="text-center py-5">
//           <p className="text-muted mb-3">Inga mallar tillgängliga</p>
//           <button
//             className="btn btn-outline-primary"
//             onClick={() => onSelect('custom')}
//           >
//             Skapa anpassad verifikation
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div>
//       <style>{`
//         .template-scroll-container {
//           height: 500px;
//           overflow-y: auto;
//           overflow-x: hidden;
//           position: relative;
//         }

//         /* Custom scrollbar styling */
//         .template-scroll-container::-webkit-scrollbar {
//           width: 6px;
//         }

//         .template-scroll-container::-webkit-scrollbar-track {
//           background: transparent;
//           border-radius: 3px;
//         }

//         .template-scroll-container::-webkit-scrollbar-thumb {
//           background: transparent;
//           border-radius: 3px;
//           transition: background 0.2s ease;
//         }

//         .template-scroll-container:hover::-webkit-scrollbar-thumb {
//           background: #cbd5e0;
//         }

//         .template-scroll-container:hover::-webkit-scrollbar-thumb:hover {
//           background: #a0aec0;
//         }

//         /* Firefox scrollbar styling */
//         .template-scroll-container {
//           scrollbar-width: thin;
//           scrollbar-color: transparent transparent;
//         }

//         .template-scroll-container:hover {
//           scrollbar-color: #cbd5e0 transparent;
//         }

//         .category-pills-container {
//           overflow-x: auto;
//           overflow-y: hidden;
//           white-space: nowrap;
//           padding-bottom: 4px;
//           margin-bottom: -4px;
//         }

//         .category-pills-container::-webkit-scrollbar {
//           height: 4px;
//         }

//         .category-pills-container::-webkit-scrollbar-track {
//           background: transparent;
//         }

//         .category-pills-container::-webkit-scrollbar-thumb {
//           background: transparent;
//         }

//         .category-pills-container:hover::-webkit-scrollbar-thumb {
//           background: #cbd5e0;
//           border-radius: 2px;
//         }

//         .template-card {
//           border: 1px solid #e2e8f0;
//           border-radius: 8px;
//           background: white;
//           transition: all 0.2s ease;
//           cursor: pointer;
//           min-height: 160px;
//         }

//         .template-card:hover {
//           border-color: var(--bs-primary);
//           box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
//           transform: translateY(-1px);
//         }

//         .template-card.active-discrete {
//           border-color: var(--bs-primary);
//           background: #f8faff;
//           box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
//         }

//         .sticky-section-header {
//           position: sticky;
//           top: 0;
//           z-index: 5;
//           background: rgba(255, 255, 255, 0.95);
//           backdrop-filter: blur(8px);
//           border-radius: 8px;
//           padding: 12px 16px;
//           margin: 0 0 16px 0;
//           border: 1px solid #e9ecef;
//           box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
//         }
//       `}</style>

//       <h2 className="fs-4 fw-medium mb-3">Välj Mall</h2>
//       <p className="text-muted mb-4">
//         Välj en mall för din {entryType === 'income' ? 'intäkts' : entryType === 'expense' ? 'kostnads' : entryType === 'transfer' ? 'överförings' : 'övriga'}-verifikation
//         {allTemplates.length > 0 && ` (${allTemplates.length} tillgängliga)`}
//       </p>

//       {/* Search and filter controls */}
//       <div className="row g-3 mb-4">
//         <div className="col-md-6">
//           <div className="input-group">
//             <span className="input-group-text bg-white border-end-0">
//               <Search size={16} />
//             </span>
//             <input
//               type="text"
//               className="form-control border-start-0"
//               placeholder="Sök mallar..."
//               value={searchQuery}
//               onChange={(e) => setSearchQuery(e.target.value)}
//               style={{ boxShadow: 'none' }}
//             />
//           </div>
//         </div>

//         <div className="col-md-6">
//           <select
//             className="form-select"
//             value={categoryFilter}
//             onChange={(e) => setCategoryFilter(e.target.value)}
//             style={{ boxShadow: 'none' }}
//           >
//             <option value="alla">Alla Kategorier</option>
//             {availableCategories.map((category) => (
//               <option key={category} value={category}>
//                 {formatCategory(category)}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>

//       {/* Category filter pills */}
//       {availableCategories.length > 0 && (
//         <div className="mb-4">
//           <div className="category-pills-container d-flex gap-2">
//             <button
//               className={`btn btn-sm flex-shrink-0 ${categoryFilter === 'alla' ? 'btn-primary' : 'btn-outline-secondary'}`}
//               onClick={() => setCategoryFilter('alla')}
//             >
//               Alla
//             </button>
//             {availableCategories.map((category) => (
//               <button
//                 key={category}
//                 className={`btn btn-sm flex-shrink-0 ${categoryFilter === category ? 'btn-primary' : 'btn-outline-secondary'}`}
//                 onClick={() => setCategoryFilter(category)}
//               >
//                 {formatCategory(category)}
//               </button>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Template selection area with category-based sticky headers */}
//       <div className="template-scroll-container">
//         {noTemplatesMatch ? (
//           <div className="text-center py-5">
//             <div className="text-muted mb-3">
//               <Search size={48} className="opacity-50 mb-3" />
//               <p className="fs-5">Inga mallar matchar dina sökkriterier</p>
//               <p className="small">Försök justera dina söktermer eller filter</p>
//             </div>
//             <button
//               className="btn btn-outline-secondary"
//               onClick={() => {
//                 setSearchQuery("");
//                 setCategoryFilter("alla");
//               }}
//             >
//               Återställ filter
//             </button>
//           </div>
//         ) : (
//           // Show templates grouped by category with sticky headers
//           Object.keys(templatesByCategory).map((category) => (
//             <div key={category} className="mb-5">
//               <div className="sticky-section-header">
//                 <div className="d-flex align-items-center">
//                   <h3 className="fs-5 fw-medium mb-0 text-dark">
//                     {formatCategory(category)} Mallar
//                   </h3>
//                   <div className="flex-grow-1 border-bottom ms-3 opacity-30"></div>
//                   <small className="text-muted ms-2">
//                     {templatesByCategory[category].length} mall{templatesByCategory[category].length !== 1 ? 'ar' : ''}
//                   </small>
//                 </div>
//               </div>

//               <div className="row g-3 mb-4">
//                 {templatesByCategory[category].map((template) => (
//                   <div key={template.id} className="col-12 col-lg-6">
//                     <div
//                       className={`template-card h-100 position-relative ${selectedTemplate === template.id ? 'active-discrete' : ''}`}
//                       onClick={() => onSelect(template.id)}
//                     >
//                       {selectedTemplate === template.id && (
//                         <div className="position-absolute top-0 end-0 m-2 text-primary">
//                           <Check size={20} strokeWidth={3} />
//                         </div>
//                       )}
//                       <div className="p-3">
//                         <div className="d-flex justify-content-between align-items-start mb-2">
//                           <h5 className="card-title fs-6 m-0 pe-2">{template.name}</h5>
//                         </div>
//                         <p className="card-text small text-muted mb-3 lh-sm">{template.description}</p>
//                         <div className="small mb-2">
//                           {template.accounts && template.accounts.slice(0, 3).map((account, index) => (
//                             <span key={index} className="badge bg-light text-dark me-1 mb-1 fs-7">
//                               {getAccountNumber(account)}
//                             </span>
//                           ))}
//                           {template.accounts && template.accounts.length > 3 && (
//                             <span className="badge bg-light text-muted fs-7">
//                               +{template.accounts.length - 3} till
//                             </span>
//                           )}
//                         </div>
//                         <div className="d-flex align-items-center gap-1 flex-wrap">
//                           <span className={`badge ${getBadgeColorClass(template.category)} fs-7`}>
//                             {formatCategory(template.category)}
//                           </span>
//                           {template.originalData && template.originalData.parameters && (
//                             <span className="badge bg-info fs-7">
//                               {template.originalData.parameters.length} param{template.originalData.parameters.length !== 1 ? 'etrar' : 'eter'}
//                             </span>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           ))
//         )}
//       </div>
//     </div>
//   );
// };

// export default TemplateSelectionStep;